import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Briefcase, Users, MessageSquare, Calendar, Star, Mail,
  Eye, Send, Clock, MapPin, ChevronDown, ChevronUp, ExternalLink,
  Loader2, CheckCircle, AlertCircle, Phone, User, Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  business_leads: { icon: Briefcase, label: 'Business Leads', color: 'text-blue-400 bg-blue-500/20' },
  partnerships: { icon: Users, label: 'Partnerships', color: 'text-purple-400 bg-purple-500/20' },
  consulting_requests: { icon: MessageSquare, label: 'Consulting', color: 'text-amber-400 bg-amber-500/20' },
  invitations: { icon: Mail, label: 'Invitations', color: 'text-green-400 bg-green-500/20' },
  events: { icon: Calendar, label: 'Events', color: 'text-pink-400 bg-pink-500/20' },
  others: { icon: Star, label: 'Others', color: 'text-zinc-400 bg-zinc-500/20' }
};

export default function MyOpportunities() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOpp, setExpandedOpp] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  
  useEffect(() => {
    fetchMyOpportunities();
  }, [pagination.page]);
  
  const fetchMyOpportunities = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/connect/my-opportunities?page=${pagination.page}&limit=10`,
        { withCredentials: true }
      );
      setOpportunities(res.data.opportunities || []);
      setPagination({
        page: res.data.page,
        total: res.data.total,
        totalPages: res.data.total_pages
      });
    } catch (err) {
      console.error('Failed to fetch opportunities:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleExpand = (oppId) => {
    setExpandedOpp(expandedOpp === oppId ? null : oppId);
  };
  
  if (loading && opportunities.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C41E3A]" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Link to="/connect" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Connect
          </Link>
          <h1 className="text-2xl font-bold">My Opportunities</h1>
          <p className="text-zinc-400 mt-1">
            View and manage your posted opportunities and track responses
          </p>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
            <div className="text-2xl font-bold">{pagination.total}</div>
            <div className="text-sm text-zinc-400">Total Posted</div>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
            <div className="text-2xl font-bold text-emerald-400">
              {opportunities.filter(o => o.status === 'live').length}
            </div>
            <div className="text-sm text-zinc-400">Live</div>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="text-2xl font-bold text-amber-400">
              {opportunities.filter(o => o.status === 'pending_review').length}
            </div>
            <div className="text-sm text-zinc-400">Pending Review</div>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-400">
              {opportunities.reduce((sum, o) => sum + (o.response_count || 0), 0)}
            </div>
            <div className="text-sm text-zinc-400">Total Responses</div>
          </div>
        </div>
        
        {/* Opportunities List */}
        {opportunities.length === 0 ? (
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-12 text-center">
            <Briefcase className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Opportunities Posted Yet</h3>
            <p className="text-zinc-400 mb-6">
              Post your first opportunity to connect with potential partners and leads.
            </p>
            <Link
              to="/connect/post"
              className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold"
            >
              Post Your First Opportunity
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {opportunities.map(opp => {
              const config = CATEGORY_CONFIG[opp.category] || CATEGORY_CONFIG.others;
              const Icon = config.icon;
              const isExpanded = expandedOpp === opp.id;
              
              return (
                <div key={opp.id} className="bg-zinc-800/50 border border-zinc-700 rounded-xl overflow-hidden">
                  {/* Opportunity Header */}
                  <div 
                    className="p-5 cursor-pointer hover:bg-zinc-800/70 transition-colors"
                    onClick={() => toggleExpand(opp.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`p-1.5 rounded ${config.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <h3 className="font-semibold text-lg">{opp.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            opp.status === 'live' ? 'bg-emerald-500/20 text-emerald-400' :
                            opp.status === 'pending_review' ? 'bg-amber-500/20 text-amber-400' :
                            opp.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {opp.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                          {opp.sector && <span>{opp.sector}</span>}
                          {opp.geography && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {opp.geography}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {opp.views || 0} views
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(opp.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Response Count Badge */}
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                          opp.response_count > 0 
                            ? 'bg-[#C41E3A]/20 text-[#C41E3A]' 
                            : 'bg-zinc-700 text-zinc-400'
                        }`}>
                          <Send className="w-4 h-4" />
                          <span className="font-semibold">{opp.response_count || 0}</span>
                          <span className="text-sm">Responses</span>
                        </div>
                        
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-zinc-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-zinc-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Responses */}
                  {isExpanded && (
                    <div className="border-t border-zinc-700 p-5 bg-zinc-900/50">
                      {opp.responses && opp.responses.length > 0 ? (
                        <div className="space-y-4">
                          <h4 className="font-semibold flex items-center gap-2">
                            <Send className="w-4 h-4 text-[#C41E3A]" />
                            Responses ({opp.responses.length})
                          </h4>
                          
                          {opp.responses.map((resp, idx) => (
                            <div key={idx} className="bg-zinc-800 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <p className="font-semibold text-lg">{resp.responder_name}</p>
                                  <p className="text-zinc-400">{resp.responder_company}</p>
                                  {resp.responder_designation && (
                                    <p className="text-sm text-zinc-500">{resp.responder_designation}</p>
                                  )}
                                </div>
                                <div className="text-right">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    resp.source === 'referral' ? 'bg-purple-500/20 text-purple-400' :
                                    resp.source === 'admin' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-zinc-600 text-zinc-300'
                                  }`}>
                                    {resp.source === 'referral' ? 'Via Referral' : 
                                     resp.source === 'admin' ? 'Admin' : 'Subscriber'}
                                  </span>
                                  <p className="text-xs text-zinc-500 mt-1">
                                    {new Date(resp.created_at).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Contact Info */}
                              <div className="flex flex-wrap gap-4 text-sm mb-3">
                                <a 
                                  href={`mailto:${resp.responder_email}`}
                                  className="flex items-center gap-1 text-[#C41E3A] hover:underline"
                                >
                                  <Mail className="w-4 h-4" />
                                  {resp.responder_email}
                                </a>
                                {resp.responder_phone && (
                                  <a 
                                    href={`tel:${resp.responder_phone}`}
                                    className="flex items-center gap-1 text-zinc-400 hover:text-white"
                                  >
                                    <Phone className="w-4 h-4" />
                                    {resp.responder_phone}
                                  </a>
                                )}
                              </div>
                              
                              {/* Message */}
                              {resp.message && (
                                <div className="bg-zinc-900/50 rounded-lg p-3 text-zinc-300">
                                  <p className="text-sm italic">"{resp.message}"</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Send className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                          <p className="text-zinc-400">No responses yet</p>
                          {opp.status === 'live' && (
                            <p className="text-sm text-zinc-500 mt-1">
                              Share your opportunity to get more visibility
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="mt-4 pt-4 border-t border-zinc-700 flex gap-3">
                        <Link
                          to={`/connect?opp=${opp.id}`}
                          className="text-sm text-[#C41E3A] hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View Public Page
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={() => setPagination(p => ({...p, page: p.page - 1}))}
              disabled={pagination.page === 1}
              className="px-4 py-2 bg-zinc-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-zinc-400">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination(p => ({...p, page: p.page + 1}))}
              disabled={pagination.page >= pagination.totalPages}
              className="px-4 py-2 bg-zinc-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
